/**
 * @fileoverview "Weekly slot" chip for StudentProfile / ClassDetail (Spec 04):
 * shows the student's or class's recurring lesson time when one exists, or a
 * quiet "Set weekly time" link. Management happens on /schedule — this is
 * just the glanceable pointer. Renders nothing while the scheduling flag is
 * off or the slots are still loading.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SCHEDULING_ENABLED, SCHEDULER_VIEW_ENABLED } from "@/lib/featureFlags";
import { isoDate, mondayOf, addDays, slotLabel, formatTime12h, recordHrefForOccurrence, shortDateLabel } from "@/lib/schedule";
import { Icon } from "@/components/studio/primitives";

export function WeeklySlotChip({ studentId, classId }) {
  const navigate = useNavigate();
  const [slots, setSlots] = useState(null); // null = loading
  const [complete, setComplete] = useState(true);

  useEffect(() => {
    if (!SCHEDULING_ENABLED) return undefined;
    const controller = new AbortController();
    if (SCHEDULER_VIEW_ENABLED) {
      api.get('/scheduler/teaching-next', { signal: controller.signal, params: studentId ? { student_id: studentId } : { class_id: classId } })
        .then(({ data }) => { setSlots(data.occurrences || []); setComplete(data.complete !== false); })
        .catch(() => { if (!controller.signal.aborted) { setSlots([]); setComplete(false); } });
      return () => controller.abort();
    }
    const monday = mondayOf(new Date());
    api.get(`/schedule?from=${isoDate(monday)}&to=${isoDate(addDays(monday, 6))}`, { signal: controller.signal })
      .then((res) => setSlots(res.data?.slots || []))
      .catch(() => setSlots([]));
    return () => controller.abort();
  }, [studentId, classId]);

  if (!SCHEDULING_ENABLED || slots === null) return null;

  const matching = slots.filter((s) =>
    studentId ? (s.student_ids || [s.student_id]).includes(studentId) : s.class_id === classId
  );

  return (
    <button
      type="button"
      onClick={() => navigate(SCHEDULER_VIEW_ENABLED ? (complete && (matching[0]?.recordable || matching[0]?.attendance_reviewable) ? recordHrefForOccurrence(matching[0]) : "/settings") : "/schedule")}
      data-testid="weekly-slot-chip"
      className="flex w-full items-center gap-2.5 rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-3.5 py-2.5 text-left transition-colors hover:bg-[var(--studio-interactive)]"
    >
      <Icon name="calendar" size={15} className="text-[var(--accent-yellow-text)]" />
      <span className="flex-1 truncate font-sans text-[13px] text-[var(--text-secondary)]">
        {SCHEDULER_VIEW_ENABLED ? (!complete ? "Scheduling needs a refresh · Manage scheduling" : matching.length
          ? `Next lesson: ${shortDateLabel(matching[0].effective_date || matching[0].date)}, ${formatTime12h(matching[0].time_local)} · ${matching[0].context_label || "Unassigned"}`
          : "No lesson in the next 31 days · Manage scheduling")
          : matching.length > 0 ? `Weekly slot: ${slotLabel(matching[0])}` : "Set weekly time"}
      </span>
      <Icon name="chevronRight" size={14} className="text-[var(--text-tertiary)]" />
    </button>
  );
}

export default WeeklySlotChip;
